import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createForm } from '../api/form-builder-api'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { FORM_ICONS, DEFAULT_FORM_ICON } from '@/lib/utils/lucide-icon-catalog'
import { IconPicker } from '@/components/shared/icon-picker'

const schema = z.object({
  title: z.string().min(1, 'عنوان الزامی است'),
  instanceType: z.enum(['single', 'repeatable']),
  icon: z.string().default(DEFAULT_FORM_ICON),
})

type FormData = z.infer<typeof schema>

interface CreateFormModalProps {
  projectId: string
  groupId: string
  open: boolean
  onClose: () => void
}

export function CreateFormModal({ projectId, groupId, open, onClose }: CreateFormModalProps) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', instanceType: 'single', icon: DEFAULT_FORM_ICON },
  })

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      createForm(projectId, groupId, user!.id, {
        title: data.title,
        instanceType: data.instanceType,
        icon: data.icon,
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['form-groups', projectId] })
      onClose()
      navigate(`/projects/${projectId}/form-builder/${result.id}`)
    },
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>ایجاد فرم جدید</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>عنوان فرم</FormLabel>
                  <FormControl>
                    <Input placeholder="مثال: مشخصات فردی" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instanceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>نوع فرم</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">تک‌نمونه — یک‌بار پر می‌شود</SelectItem>
                      <SelectItem value="repeatable">تکرارپذیر — چند نمونه</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>آیکون</FormLabel>
                  <IconPicker
                    value={field.value}
                    onChange={(key) => field.onChange(key || DEFAULT_FORM_ICON)}
                    icons={FORM_ICONS}
                    clearable={false}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>انصراف</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'در حال ایجاد...' : 'ایجاد و ویرایش'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
